import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { SkeletonPageBody } from '../components/ui';
import useMemberSurveillanceOverview from '../hooks/useMemberSurveillanceOverview';
import useMyInputsProfile from '../hooks/useMyInputsProfile';
import useMelioIntelligence from '../hooks/useMelioIntelligence';
import { LabResultRecord } from '../types/labs';
import { listMemberVisibleLabResults, listObservationsForUser } from '../services/observationService';
import type { ObservationRecord } from '../types/observations';
import { buildWearableInsightBundle } from '../services/wearableInsightService';
import { LoadFailureCard, PageHeader, UploadIcon } from '../components/layout/memberPrimitives';
import MemberBiomarkerTrendGrid from '../components/dashboard/MemberBiomarkerTrendGrid';
import DailyBiteCorrelationsSection from '../components/dailyBite/DailyBiteCorrelationsSection';
import { DEFAULT_DASHBOARD_METRIC_IDS, type WearableMetricId } from '../data/wearableMetrics';
import { sanitizeDashboardMetricIds } from '../services/memberWearableMetricsService';
import { buildSignalCards, getLatestLabResult } from '../utils/memberEngagement';
import { resolveBiomarker } from '../data/biomarkerCatalogue';
import { computeBiomarkerStatus } from '../utils/biomarkerStatus';
import BiomarkerRangeBar from '../components/member/BiomarkerRangeBar';
import BiomarkerStatusPill from '../components/member/BiomarkerStatusPill';

export default function MelioIntelligencePage() {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const { profile: myInputsProfile, loading: inputsLoading } = useMyInputsProfile(currentUser?.uid);
  const { latestPlan, openPrompts, familyTree, loading: overviewLoading } = useMemberSurveillanceOverview(currentUser?.uid);
  const [labResults, setLabResults] = useState<LabResultRecord[]>([]);
  const [wearableObservations, setWearableObservations] = useState<ObservationRecord[]>([]);
  const [memberSex, setMemberSex] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dashboardMetricIds, setDashboardMetricIds] = useState<WearableMetricId[]>(DEFAULT_DASHBOARD_METRIC_IDS);
  const [savingDashboardMetrics, setSavingDashboardMetrics] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  // Results left this page for /results, so the tab is now a redirect rather
  // than a view. 'uploads' is the name it had before 'results'; both are still
  // honoured so bookmarks and any link already sent to a member keep working.
  const tabParam = searchParams.get('tab');
  const resultsRequested = tabParam === 'results' || tabParam === 'uploads';

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      setLoading(true);
      try {
        // Tracked so the results tab can say "we couldn't read this" instead of
        // silently rendering as though the member has no results at all.
        const [visibleLabResults, canonicalWearableObservations] = await Promise.all([
          listMemberVisibleLabResults(currentUser.uid),
          listObservationsForUser(currentUser.uid, 'wearable'),
        ]);
        setLabResults(visibleLabResults);
        setWearableObservations(canonicalWearableObservations);
        setLoadFailed(false);
        try {
          const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
          const userData = userSnap.exists() ? (userSnap.data() as Record<string, any>) : null;
          setDashboardMetricIds(sanitizeDashboardMetricIds(userData?.dashboardPreferences?.wearableMetricIds));
          setMemberSex(userData?.sexAtBirth || userData?.sex || userData?.gender || null);
        } catch (prefError) {
          console.warn('Could not load dashboard metric preferences', prefError);
        }
      } catch (error) {
        console.error('Error loading intelligence context:', error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentUser, reloadToken]);

  const handleSaveDashboardMetrics = async (metricIds: WearableMetricId[]) => {
    if (!currentUser) return;
    setSavingDashboardMetrics(true);
    try {
      await setDoc(
        doc(db, 'users', currentUser.uid),
        {
          dashboardPreferences: { wearableMetricIds: metricIds },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setDashboardMetricIds(metricIds);
    } finally {
      setSavingDashboardMetrics(false);
    }
  };
  const wearableInsightBundle = useMemo(
    () => buildWearableInsightBundle(wearableObservations),
    [wearableObservations]
  );

  const intelligenceContext = useMemo(() => {
    if (!currentUser) return null;
    return {
      myInputsProfile,
      latestPlan,
      openPrompts,
      familyTree,
      labResults,
    };
  }, [currentUser, familyTree, labResults, latestPlan, myInputsProfile, openPrompts]);

  const { items, loading: intelligenceLoading } = useMelioIntelligence(intelligenceContext);

  const signalCards = useMemo(
    () =>
      buildSignalCards({
        profile: null,
        myInputsProfile,
        medicalDocCount: 0,
        wearableDocCount: wearableObservations.length,
        labResults,
        latestPlan,
        openPrompts,
        familyTree,
      }),
    [familyTree, labResults, latestPlan, myInputsProfile, openPrompts, wearableObservations.length],
  );

  // Status + range for the "Latest biomarker" signal card — same record the
  // card's title uses (getLatestLabResult), so the bar and title always agree.
  const latestBiomarker = useMemo(() => {
    const record = getLatestLabResult(labResults);
    if (!record) return null;
    const meta = resolveBiomarker({ biomarkerKey: record.biomarkerKey, biomarker: record.biomarker });
    if (!meta) return null;
    return { meta, status: computeBiomarkerStatus(record, meta, memberSex) };
  }, [labResults, memberSex]);

  // Ahead of the loading gate on purpose: a member arriving on the old tab URL
  // should be moved to /results immediately, not held behind this page's reads
  // and then moved.
  if (resultsRequested) return <Navigate to="/results" replace />;

  if (loading || inputsLoading || overviewLoading || intelligenceLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <PageHeader
          breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'Intelligence' }]}
          eyebrow="Melio Intelligence"
          title="What your results mean."
          subtitle="Your trends, and the reading we think is worth your time. Your biomarkers themselves live in Results."
        />
        <SkeletonPageBody cards={3} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 melio-stagger">
      <PageHeader
        breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'Intelligence' }]}
        eyebrow="Melio Intelligence"
        title="What your results mean."
        subtitle="Your trends, and the reading we think is worth your time. Your biomarkers themselves live in Results."
        rightSlot={
          <Link
            to="/results"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border melio-lift"
            style={{ borderColor: 'var(--m-line)', backgroundColor: 'var(--m-surface)', color: 'var(--m-ink)' }}
          >
            <UploadIcon /> Open your results
          </Link>
        }
      />

      {loadFailed ? (
        <LoadFailureCard
          title="We couldn’t load your results"
          body="Your results are still on file — this is a connection problem, not a change to your record."
          onRetry={() => setReloadToken((token) => token + 1)}
        />
      ) : (
        <div className="space-y-8">
          {signalCards.length > 0 && (
            <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <div className="flex items-start justify-between gap-4 flex-col md:flex-row md:items-center mb-5">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--melio-sage)' }}>
                    Signal to meaning
                  </p>
                  <h2
                    className="text-2xl font-normal"
                    style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
                  >
                    What&rsquo;s freshest in your plan
                  </h2>
                  <p className="text-sm mt-2 max-w-2xl" style={{ color: 'var(--melio-text-muted)' }}>
                    The newest inputs &mdash; your plan version, latest biomarker, open clinical loops, family context &mdash; restated in plain language with where to look next.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {signalCards.map((card) => (
                  <Link
                    key={card.id}
                    to={card.path}
                    className="melio-lift block rounded-2xl p-5"
                    style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}
                  >
                    <p
                      className="text-xs uppercase tracking-[0.18em] mb-2"
                      style={{ color: 'var(--melio-text-muted)' }}
                    >
                      {card.eyebrow}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3
                        className="text-lg font-normal leading-snug"
                        style={{
                          fontFamily: "var(--melio-display)",
                          color: 'var(--melio-charcoal)',
                        }}
                      >
                        {card.title}
                      </h3>
                      {card.id === 'lab' && latestBiomarker && <BiomarkerStatusPill status={latestBiomarker.status} />}
                    </div>
                    {card.id === 'lab' && latestBiomarker && latestBiomarker.status.hasBar && (
                      <div className="mb-3">
                        <BiomarkerRangeBar meta={latestBiomarker.meta} status={latestBiomarker.status} />
                      </div>
                    )}
                    <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--melio-charcoal)' }}>
                      {card.detail}
                    </p>
                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span style={{ color: 'var(--melio-text-muted)' }}>{card.meta}</span>
                      <span className="font-medium" style={{ color: 'var(--melio-olive-deep)' }}>
                        {card.ctaLabel} →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {currentUser ? (
            <DailyBiteCorrelationsSection
              uid={currentUser.uid}
              wearableObservations={wearableObservations}
            />
          ) : null}

          {wearableObservations.length > 0 && (
            <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <div className="mb-5">
                <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--melio-sage)' }}>
                  Your trends
                </p>
                <h2
                  className="text-2xl font-normal"
                  style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
                >
                  Biomarker trend grid
                </h2>
                <p className="text-sm mt-2" style={{ color: 'var(--melio-text-muted)' }}>
                  Pick the wearable metrics you want to watch. Tap any card to see the full trend and how we&rsquo;re reading it.
                </p>
              </div>
              <MemberBiomarkerTrendGrid
                observations={wearableObservations}
                selectedMetricIds={dashboardMetricIds}
                onSaveSelection={handleSaveDashboardMetrics}
                savingSelection={savingDashboardMetrics}
              />
            </section>
          )}

          {wearableObservations.length > 0 && (
            <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <div className="flex items-start justify-between gap-4 flex-col md:flex-row md:items-center mb-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--melio-sage)' }}>
                    Wearable interpretation
                  </p>
                  <h2 className="text-2xl font-normal" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
                    {wearableInsightBundle.patientInterpretation.title}
                  </h2>
                </div>
                <span
                  className="px-3 py-1.5 rounded-full text-xs uppercase tracking-wide"
                  style={{
                    backgroundColor: wearableInsightBundle.patientInterpretation.tone === 'action'
                      ? 'rgba(174, 85, 85, 0.12)'
                      : wearableInsightBundle.patientInterpretation.tone === 'watch'
                        ? 'rgba(196, 164, 132, 0.18)'
                        : 'rgba(79, 93, 24, 0.14)',
                    color: 'var(--melio-charcoal)',
                  }}
                >
                  {wearableInsightBundle.patientInterpretation.tone === 'action'
                    ? 'Act on this'
                    : wearableInsightBundle.patientInterpretation.tone === 'watch'
                      ? 'Worth tightening up'
                      : 'Stable for now'}
                </span>
              </div>

              <p className="text-sm md:text-base leading-relaxed mb-6" style={{ color: 'var(--melio-charcoal)' }}>
                {wearableInsightBundle.patientInterpretation.summary}
              </p>

              <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-6">
                <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                  <p className="text-xs uppercase tracking-wide mb-3" style={{ color: 'var(--melio-text-muted)' }}>
                    Recent wearable window
                  </p>
                  <div className="space-y-3 text-sm" style={{ color: 'var(--melio-charcoal)' }}>
                    {wearableInsightBundle.stats.avgSleepDuration != null && (
                      <div className="flex items-center justify-between gap-4">
                        <span>Average sleep</span>
                        <span>{wearableInsightBundle.stats.avgSleepDuration.toFixed(1)}h</span>
                      </div>
                    )}
                    {wearableInsightBundle.stats.avgSleepEfficiency != null && (
                      <div className="flex items-center justify-between gap-4">
                        <span>Sleep efficiency</span>
                        <span>{wearableInsightBundle.stats.avgSleepEfficiency.toFixed(0)}%</span>
                      </div>
                    )}
                    {wearableInsightBundle.stats.avgRecovery != null && (
                      <div className="flex items-center justify-between gap-4">
                        <span>Average recovery</span>
                        <span>{wearableInsightBundle.stats.avgRecovery.toFixed(0)}%</span>
                      </div>
                    )}
                    {wearableInsightBundle.stats.avgRestingHr != null && (
                      <div className="flex items-center justify-between gap-4">
                        <span>Average resting HR</span>
                        <span>{wearableInsightBundle.stats.avgRestingHr.toFixed(0)} bpm</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide mb-3" style={{ color: 'var(--melio-text-muted)' }}>
                    Suggested next steps
                  </p>
                  <div className="space-y-3">
                    {wearableInsightBundle.patientInterpretation.nextSteps.map((step) => (
                      <div
                        key={step}
                        className="rounded-2xl border px-4 py-3 text-sm"
                        style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
                      >
                        {step}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs leading-relaxed mt-4" style={{ color: 'var(--melio-text-muted)' }}>
                    {wearableInsightBundle.patientInterpretation.caution}
                  </p>
                </div>
              </div>
            </section>
          )}

          {items.length === 0 ? (
            <section className="rounded-3xl border p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <h2 className="m-title mb-3">Nothing to read yet.</h2>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
                We write here when there is something worth your attention — and we mark the pieces
                that relate to your own results, rather than sending you everything.
              </p>
            </section>
          ) : (
            <section className="grid lg:grid-cols-2 gap-6">
              {items.map((item) => (
                <article key={item.id || item.slug} className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex flex-wrap gap-2">
                      {item.topicTags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full text-xs uppercase tracking-wide"
                          style={{ backgroundColor: 'rgba(79, 93, 24, 0.12)', color: 'var(--melio-charcoal)' }}
                        >
                          {tag.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                    {item.personalizationReason && (
                      <span
                        className="px-3 py-1.5 rounded-full text-xs"
                        style={{ backgroundColor: 'rgba(84, 112, 146, 0.12)', color: 'var(--melio-charcoal)' }}
                      >
                        {item.personalizationReason}
                      </span>
                    )}
                  </div>

                  <h2 className="text-2xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
                    {item.title}
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: 'var(--melio-charcoal)' }}>
                    {item.melioSummary}
                  </p>
                  <div className="rounded-2xl p-4 mb-4" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                    <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                      Why this matters
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
                      {item.whyThisMatters}
                    </p>
                  </div>
                  {item.sourceUrl ? (
                    <a
                      href={item.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium"
                      style={{ color: 'var(--melio-sage)' }}
                    >
                      Read source{item.sourceName ? `: ${item.sourceName}` : ''}
                    </a>
                  ) : item.sourceName ? (
                    <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                      Source: {item.sourceName}
                    </p>
                  ) : null}
                </article>
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
